import { CircleAlert, Info } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Block, FilePicker, VerdictBadge } from './PrimaNotaBlocks';
import type { Verdict } from './prima-nota-derived';
import type { Gap, Readiness } from './prima-nota-model';

type ProfileFormProps = {
  profileNote: string;
  onNoteChange: (note: string) => void;
  busy: boolean;
  locked: boolean;
  onPick: (file: File) => void;
};

// One form for the first profile and for every later revision: the same note, the same file, the
// same check before it is stored.
export function ProfileForm(props: ProfileFormProps) {
  const { t } = useTranslation('finance');
  return (
    <>
      <div className="max-w-md space-y-2">
        <label className="field-label" htmlFor="prima-nota-profile-note">
          {t('primaNota.profile.noteLabel')}
        </label>
        <Input
          id="prima-nota-profile-note"
          value={props.profileNote}
          onChange={(event) => props.onNoteChange(event.target.value)}
        />
        <p className="text-muted-foreground text-xs">{t('primaNota.profile.noteHint')}</p>
      </div>
      <FilePicker
        label={t('primaNota.profile.pick')}
        hint={t('primaNota.profile.pickHint')}
        choose={t('primaNota.supply.choose')}
        busy={props.busy}
        locked={props.locked}
        busyLabel={t('primaNota.supply.uploading')}
        testId="upload-profile"
        onPick={props.onPick}
      />
    </>
  );
}

/** The step that stops everything else while no profile exists. */
export function ProfileMissingBlock({ profileForm }: { profileForm: ReactNode }) {
  const { t } = useTranslation('finance');
  return (
    <Block
      id="prima-nota-profile"
      title={t('primaNota.profile.heading')}
      tone="error"
      testId="prima-nota-profile-step"
    >
      <p className="text-sm leading-6">{t('primaNota.profile.explain')}</p>
      {profileForm}
    </Block>
  );
}

function GapRow({ gap }: { gap: Gap }) {
  const { t } = useTranslation('finance');
  return (
    <li
      className="flex items-start gap-3 px-3 py-3 text-sm leading-6"
      data-testid={`gap-${gap.code}`}
    >
      {gap.blocking ? (
        <CircleAlert aria-hidden="true" className="text-destructive mt-1 size-4 shrink-0" />
      ) : (
        <Info aria-hidden="true" className="text-warning mt-1 size-4 shrink-0" />
      )}
      <div className="min-w-0 flex-1 space-y-1">
        <span className={gap.blocking ? 'status-badge error' : 'status-badge warning'}>
          {gap.blocking ? t('primaNota.check.blockingBadge') : t('primaNota.check.warningBadge')}
        </span>
        <p>{gap.detail}</p>
      </div>
    </li>
  );
}

type CheckBlockProps = {
  data: Readiness;
  verdict: Verdict;
  verdictText: string;
  locked: boolean;
  profileBusy: boolean;
  revisingProfile: boolean;
  onRevise: () => void;
  onCancelRevise: () => void;
  profileForm: ReactNode;
};

function ProfileRevision(props: CheckBlockProps & { revision: number }) {
  const { t } = useTranslation('finance');
  return (
    <div
      className="bg-background border-border space-y-3 rounded-md border p-4"
      data-testid="prima-nota-profile-revision"
    >
      <p className="text-sm font-semibold">
        {t('primaNota.profile.reviseHeading', { revision: props.revision })}
      </p>
      <p className="text-sm leading-6">{t('primaNota.profile.reviseExplain')}</p>
      {props.profileForm}
      <Button
        variant="outline"
        size="sm"
        disabled={props.profileBusy}
        onClick={props.onCancelRevise}
      >
        {t('primaNota.profile.reviseCancel')}
      </Button>
    </div>
  );
}

/** Step one: what the month still lacks, and the profile it is read with. */
export function CheckBlock(props: CheckBlockProps) {
  const { data, verdict, verdictText, locked, revisingProfile } = props;
  const { t } = useTranslation('finance');
  return (
    <Block
      id="prima-nota-check"
      title={t('primaNota.steps.check')}
      aside={<VerdictBadge verdict={verdict} />}
    >
      <p className="text-sm" data-testid="prima-nota-verdict">
        {verdictText}
      </p>
      {data.gaps.length > 0 && (
        <ul className="divide-border border-border divide-y rounded-md border">
          {data.gaps.map((gap, index) => (
            // A code and a subject can repeat since each format of a statement has its own
            // archive gaps: the PDF and the CSV of one account are two lines.
            <GapRow key={`${gap.code}:${gap.subject ?? ''}:${index}`} gap={gap} />
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-muted-foreground text-xs">
          {data.profileRevision === null
            ? t('primaNota.check.profileMissing')
            : t('primaNota.check.profileRevision', { revision: data.profileRevision })}
        </p>
        {data.profileRevision !== null && !revisingProfile && (
          <Button
            variant="ghost"
            size="sm"
            disabled={locked}
            data-testid="revise-profile"
            onClick={props.onRevise}
          >
            {t('primaNota.profile.revise')}
          </Button>
        )}
      </div>
      {data.profileRevision !== null && revisingProfile && (
        <ProfileRevision {...props} revision={data.profileRevision} />
      )}
    </Block>
  );
}
